import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Plus, Trash2, Tag, ToggleLeft, ToggleRight } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inputCls =
  'w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#7B1E2B] focus:border-transparent';

const emptyForm = { code: '', type: 'percent', value: '', expires_at: '', max_uses: '' };

export const AdminDiscounts = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    fetch(`${API_URL}/api/discount-codes`, { credentials: 'include' })
      .then((r) => r.json())
      .then((data) => setCodes(Array.isArray(data) ? data : []))
      .catch(() => setCodes([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.value) {
      toast.error('Code and value are required');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/discount-codes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: form.code.trim().toUpperCase(),
          type: form.type,
          value: parseFloat(form.value),
          active: true,
          expires_at: form.expires_at,
          max_uses: parseInt(form.max_uses, 10) || 0,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create code');
      toast.success(`Code ${data.code} created`);
      setForm(emptyForm);
      load();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (c) => {
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/${c.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ code: c.code, type: c.type, value: c.value, active: !c.active, expires_at: c.expires_at || '', max_uses: c.max_uses || 0 }),
      });
      if (!res.ok) throw new Error();
      setCodes((prev) => prev.map((x) => (x.id === c.id ? { ...x, active: !c.active } : x)));
      toast.success(`${c.code} ${!c.active ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error('Failed to update code');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete promo code "${c.code}"?`)) return;
    try {
      const res = await fetch(`${API_URL}/api/discount-codes/${c.id}`, { method: 'DELETE', credentials: 'include' });
      if (!res.ok) throw new Error();
      toast.success('Code deleted');
      load();
    } catch {
      toast.error('Failed to delete code');
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#7B1E2B]" /></div>;

  return (
    <div className="space-y-6" data-testid="admin-discounts">
      <form onSubmit={handleCreate} className="bg-white rounded-lg shadow-md p-6">
        <h3 className="font-serif font-bold text-[#001B2A] mb-4 flex items-center gap-2">
          <Plus className="h-5 w-5" /> Create Promo Code
        </h3>
        <div className="grid sm:grid-cols-5 gap-3">
          <input data-testid="discount-form-code" name="code" value={form.code} onChange={handleChange} placeholder="CODE (e.g. SUMMER10)" className={`${inputCls} uppercase`} />
          <select data-testid="discount-form-type" name="type" value={form.type} onChange={handleChange} className={inputCls}>
            <option value="percent">% off</option>
            <option value="fixed">$ off</option>
          </select>
          <input data-testid="discount-form-value" name="value" type="number" step="0.01" min="0" value={form.value} onChange={handleChange} placeholder={form.type === 'percent' ? '10 (%)' : '5.00 ($)'} className={inputCls} />
          <input data-testid="discount-form-expires" name="expires_at" type="date" value={form.expires_at} onChange={handleChange} className={inputCls} title="Expiration date (optional)" />
          <input data-testid="discount-form-maxuses" name="max_uses" type="number" min="0" step="1" value={form.max_uses} onChange={handleChange} placeholder="Max uses (0 = ∞)" className={inputCls} />
        </div>
        <button
          type="submit"
          data-testid="discount-form-save"
          disabled={saving}
          className="mt-4 flex items-center gap-2 px-5 py-2.5 bg-[#7B1E2B] text-white rounded-md font-semibold text-sm hover:bg-[#001B2A] transition-colors disabled:opacity-60"
        >
          {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create Code
        </button>
      </form>

      <div className="bg-white rounded-lg shadow-md overflow-x-auto" data-testid="discount-codes-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 text-left text-gray-500 uppercase text-xs">
              <th className="px-4 py-3">Code</th>
              <th className="px-4 py-3">Discount</th>
              <th className="px-4 py-3">Expires</th>
              <th className="px-4 py-3">Uses</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {codes.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500">No promo codes yet.</td></tr>
            )}
            {codes.map((c) => (
              <tr key={c.id} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="px-4 py-3 font-bold text-[#001B2A] flex items-center gap-2">
                  <Tag className="h-4 w-4 text-[#7B1E2B]" /> {c.code}
                </td>
                <td className="px-4 py-3 font-semibold">
                  {c.type === 'percent' ? `${c.value}% off` : `$${c.value.toFixed(2)} off`}
                </td>
                <td className="px-4 py-3 text-gray-600">{c.expires_at || 'Never'}</td>
                <td className="px-4 py-3 text-gray-600">{c.uses || 0}{c.max_uses > 0 ? ` / ${c.max_uses}` : ''}</td>
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${c.active ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-600'}`}>
                    {c.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button data-testid={`discount-toggle-${c.id}`} onClick={() => toggleActive(c)} className="p-2 text-gray-500 hover:text-[#001B2A] hover:bg-gray-100 rounded" title={c.active ? 'Deactivate' : 'Activate'}>
                      {c.active ? <ToggleRight className="h-5 w-5 text-green-600" /> : <ToggleLeft className="h-5 w-5" />}
                    </button>
                    <button data-testid={`discount-delete-${c.id}`} onClick={() => handleDelete(c)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
